import * as React from 'react'
import { CodeInputProps } from './CodeInput'
import {
  codeToString,
  emptyStringArray,
  findLastValidInput,
  stringToCode
} from './utils'

import type { TextInputProps } from '../TextInput'

type ChangeEvent = React.ChangeEvent<HTMLInputElement>
type ClipboardEvent = React.ClipboardEvent<HTMLInputElement>
type FocusEvent = React.FocusEvent<HTMLInputElement>
type KeyboardEvent = React.KeyboardEvent<HTMLInputElement>
type GeneralEvent = React.SyntheticEvent<HTMLInputElement>
type GetInputPropsReturns = TextInputProps &
  React.RefAttributes<HTMLInputElement>

const DIGIT_CHAR = /\d{1}/i
const NOT_DIGIT_CHARS = /\D/gi

export function useCodeInput(
  { length, code, name, onCodeChange }: CodeInputProps,
  ref?: React.ForwardedRef<HTMLInputElement>
) {
  // Declares it as non-null with `null!`, since it should always be assigned
  // to one of the input
  const pointer = React.useRef<HTMLInputElement>(null!)
  const [pointerIndex, setPointerIndex] = React.useState(0)
  const [isFocused, setIsFocused] = React.useState(false)
  const hasPointerMovedWithCode = React.useRef(false)
  // Type guard for `code` and `onCodeChange` across the hook
  const isStateful = !!code && !!onCodeChange

  function callOnCodeChange(copy: string[]) {
    if (isStateful) {
      const code = codeToString(copy)
      const isComplete = code.length === length
      onCodeChange(copy, isComplete)
    }
  }

  // Register the character and shift pointer to the next input
  function typeChar(index: number, str: string) {
    if (!DIGIT_CHAR.test(str) || Number.isNaN(str)) return
    if (isStateful) {
      const copy = [...code]
      // The str can be more than one character. In these edge cases, take the last character
      copy[index] = str
      callOnCodeChange(copy)
    }
    setPointerIndex(index + 1 < length ? index + 1 : index)
    hasPointerMovedWithCode.current = true
  }

  // Handles deleting characters
  function removeChar(index: number, fromRight: boolean = false) {
    let offset = 1
    if (isStateful) {
      const copy = [...code]
      if (copy[index] === '') {
        const next = fromRight ? index + 1 : index - 1
        copy[next] = ''
      } else {
        copy[index] = ''
        offset = 0
      }
      onCodeChange(copy, false)
    }
    if (fromRight) {
      setPointerIndex(index < length - 1 ? index + offset : index)
    } else {
      setPointerIndex(index > 0 ? index - offset : index)
    }
    hasPointerMovedWithCode.current = true
  }

  // Useful when hitting cmd+backspace or to provide an external action to do so
  function clearCode() {
    if (isStateful) {
      onCodeChange(emptyStringArray(length), false)
      setPointerIndex(0)
    }
  }

  // When given a full code, or multiple digits, set everything in the input and
  // shift pointer to next input after pasted code (or last input available).
  function typeFullCode(str: string) {
    if (str.length === 0) return
    if (isStateful) {
      const newInput = stringToCode(str, length)
      callOnCodeChange(newInput)
    }
  }

  // Get data from the pasted text, and only extracts the digits from it. It
  // will override any already typed code.
  function parsePasteData(e: ClipboardEvent) {
    const data = e.clipboardData.getData('text/plain')
    const prepared = data.replace(NOT_DIGIT_CHARS, '').substring(0, length)
    if (prepared.length > 0) {
      typeFullCode(prepared)
    }
  }

  function shiftPointer(position: 'start' | 'end' | 'left' | 'right') {
    setPointerIndex((prev) => {
      if (prev < length - 1) {
        if (position === 'end') return length - 1
        if (position === 'right') return prev + 1
      }
      if (prev > 0) {
        if (position === 'start') return 0
        if (position === 'left') return prev - 1
      }
      return prev
    })
  }

  // When called, this will prevent the event from continuing, in order to
  // keep only the behavior stated by the hook. This is especially useful
  // when shifting the pointer (e.g. with arrow keys), since this will be caught
  // by individual inputs to set the selection range.
  function ensureSelection(e: GeneralEvent) {
    // We don't want to prevent the default behavior for stateless component,
    // as it will be how it can delete its own character.
    if (isStateful) {
      e.preventDefault()
    }
  }

  // Auto-focus to the pointer based on current's index and select everything in
  // it to be easier to type in it or move around
  React.useEffect(() => {
    if (isFocused) {
      pointer.current?.focus()
      pointer.current?.setSelectionRange(0, 1)
    }
  }, [isFocused, pointerIndex])

  // Only when the code changes, we reset the pointer position if it doesn't
  // match the code size (based on the valid values from the array). This allows
  // to move the pointer when the code is programmatically changed (e.g.
  // clearing the code from outside).
  React.useEffect(() => {
    if (!!code && !hasPointerMovedWithCode.current) {
      setPointerIndex((prevIndex) => {
        const lastInputIndex = findLastValidInput(code)
        // Nothing in the code array
        if (lastInputIndex === -1) {
          return 0
        }
        // Pointer and code out of sync, we set pointer to the next empty input
        if (lastInputIndex !== prevIndex) {
          return lastInputIndex < length - 1
            ? lastInputIndex + 1
            : lastInputIndex
        }
        // Looking good
        return prevIndex
      })
    }
    // We mark when the pointer has been updated because of keyboard interaction
    // so we can skip this effect when that's the case. We only want to update
    // the pointer position when code changes weren't the result of a direct
    // user interaction.
    hasPointerMovedWithCode.current = false
  }, [code, length])

  // Allows to still use our internal ref for user interaction, but forward it
  // to the parent so they can use the ref pointing to the current input
  React.useImperativeHandle(ref, () => {
    return pointer.current
  })

  return {
    code,
    clearCode,
    typeFullCode,
    getInputProps(index: number): GetInputPropsReturns {
      const isFocusedInput = pointerIndex === index
      return {
        ref: isFocusedInput ? pointer : undefined,
        name: isFocusedInput ? name : undefined,
        id: isFocusedInput ? name : undefined,
        tabIndex: isFocusedInput ? 0 : -1,
        value: code?.[index],
        onFocus(e: FocusEvent) {
          if (!isFocusedInput) {
            setPointerIndex(index)
          }
          setIsFocused(true)
          e.target.setSelectionRange(0, 1)
        },
        onBlur() {
          setIsFocused(false)
        },
        onPaste(e: ClipboardEvent) {
          if (isStateful) {
            e.preventDefault()
          }
          parsePasteData(e)
        },
        onChange(e: ChangeEvent) {
          // Since `onChange` is only called when the value is different from
          // the previous one, it won't call this method and won't shift the
          // pointer. Technically, we could rely on `onKeyDown`, but that
          // requires to parse the keys and manually detect when they should be
          // considered as valid keys for the code.
          const str = e.target.value
          if (str.length > 1) {
            if (!isStateful) {
              e.target.value = str[0]
            } else {
              e.target.value = str[str.length - 1]
            }
          }

          typeChar(index, e.target.value)
        },
        onKeyDown(e: KeyboardEvent) {
          const cmdCtrl = e.metaKey || e.ctrlKey
          if (e.key === 'Backspace') {
            if (cmdCtrl) {
              clearCode()
            } else {
              removeChar(index)
              ensureSelection(e)
            }
          }
          if (e.key === 'Delete') {
            if (cmdCtrl) {
              clearCode()
            } else {
              removeChar(index, true)
              ensureSelection(e)
            }
          }
          // Avoid calling a `setState` with a function that computes based
          // on `e.key`, as (1) it's not easily readable and (2) it can lead
          // to issues with React `SyntheticEvent`s.
          if (
            e.key === 'ArrowDown' ||
            e.key === 'End' || // Specific to Windows
            (cmdCtrl && e.key === 'ArrowRight')
          ) {
            shiftPointer('end')
            ensureSelection(e)
          }
          if (
            e.key === 'ArrowUp' ||
            e.key === 'Home' || // Specific to Windows
            (cmdCtrl && e.key === 'ArrowLeft')
          ) {
            shiftPointer('start')
            ensureSelection(e)
          }
          if (e.key === 'ArrowRight') {
            shiftPointer('right')
            ensureSelection(e)
          }
          if (e.key === 'ArrowLeft') {
            shiftPointer('left')
            ensureSelection(e)
          }
        }
      }
    }
  }
}
